<template>
  <div v-show="displayFrontend" :class="{questions__save: safeFreeze}" class="questions">
    <h1 class="questions__header">{{ $t("Invoice details") }}</h1>
    <b-form-checkbox
      id="billingNotes"
      v-model="billing.billingRemarksData"
      class="checkbox"
      name="check-button"
      switch
      @change="advancedBilling($event)">
      {{ $t("Bill on another or multiple companies") }}
    </b-form-checkbox>

    <div id="remarks_bottom" :class="{hidden: !showBillingRemarks}">
      <h2 class="questions__subheader">
        {{
          $t("Below, indicate per company how you want to be invoiced, also mention the contact details and IBAN (if applicable).")
        }}
      </h2>
      <b-form-textarea
        id="billingRemarks"
        v-model="billing.billingRemarks"
        class="questions__textarea"
        rows="3"/>
    </div>

    <div :class="{hidden: !showBillingAddress}">
      <h2 class="questions__subheader">
        {{ $t("Company name bank account holder") }}
      </h2>
      <input
        id="accountName"
        v-model="billing.accountName"
        v-b-popover.hover.right="'Officiele bedrijfsnaam zoals ingeschreven in de KVK'"
        class="form-control"
        disabled
        type="text"/>

      <h2 class="questions__subheader">
        {{ $t("Street and house number") }}
      </h2>
      <div class="inputinline">
        <input
          id="accountBillingStreet"
          v-model="billing.accountBillingStreet"
          class="form-control form-empty"
          disabled
          type="text"/>
      </div>

      <div class="accountBillingCity">
        <h2 class="questions__subheader">
          {{ $t("City") }}
        </h2>
        <input
          id="accountBillingCity"
          v-model="billing.accountBillingCity"
          class="form-control form-empty"
          disabled
          type="text"/>
      </div>

      <div class="accountBillingPostalCode">
        <h2 class="questions__subheader">
          {{ $t("Zip code") }}
        </h2>
        <input
          id="accountBillingPostalCode"
          v-model="billing.accountBillingPostalCode"
          class="form-control form-empty"
          disabled
          type="text"/>
      </div>

      <h2 class="questions__subheader">
        {{ $t("Country") }}
      </h2>
      <input
        id="accountBillingCountry"
        v-model="billing.accountBillingCountry"
        class="form-control form-empty"
        disabled
        type="text"/>

      <div :class="{hidden: !showAbroadFields || billing.accountBillingCountry === 'Germany'}">
        <h2 class="questions__subheader">
          {{ $t("VAT number") }}
        </h2>

        <b-form-input
          id="accountVatNumber"
          v-model="billing.accountVatNumber"
          :state="vatCorrect"
          @input="vatCorrect=null;"
          class="form-control"
          type="text">
        </b-form-input>
        <i v-show="vatComment!==''">{{ vatComment }}</i>
      </div>

      <h2 :title="$t('The reference number on the purchase order, this will appear on the invoice')"
          class="questions__subheader">
        {{ $t("PO number") }}
      </h2>
      <input
        v-model="billing.poNumber"
        class="form-control form-empty"
        type="text"
      />
    </div>

    <div class="hide" :class="{hidden: !showBillingContactFields}">
      <h1 class="question--marginTop questions__header">
        {{ $t("Financial contact person") }}
      </h1>
      <h2 class="questions__subheader">
        {{ $t("Name financial contact") }}
      </h2>
      <b-form-radio-group
        id="billingContactSalutation"
        v-model="billing.billingContactSalutation"
        button-variant="outline-primary"
        buttons
        class="questions-saluationinput"
        name="radio-btn-outline">
        <b-form-radio id="heer" value="heer">
          {{ $t("Mr.") }}
        </b-form-radio>
        <b-form-radio id="mevrouw" value="mevrouw">
          {{ $t("Ms.") }}
        </b-form-radio>
        <b-form-radio id="null" value="null">
          {{ $t("Other") }}
        </b-form-radio>
      </b-form-radio-group>

      <b-form-input
        id="questions-nameinput"
        v-model="billing.billingContactFirstName"
        :placeholder="$t('Firstname')"
        :state="billing.billingContactFirstName != ''">
      </b-form-input>

      <b-form-input
        id="questions-nameinput"
        v-model="billing.billingContactLastName"
        :placeholder="$t('Lastname')"
        :state="billing.billingContactLastName !== ''">
      </b-form-input>

      <h2 class="questions__subheader">
        {{ $t("Phone number financial contact") }}
      </h2>

      <b-form-input
        v-model="billing.billingContactPhone"
        :state="billing.billingContactPhone !== ''">
      </b-form-input>

      <h2 class="questions__subheader">
        {{ $t("Billing Email") }}
      </h2>
      <b-form-input
        v-model="billing.billingContactEmail"
        v-b-popover.hover.right="$t('You will receive the invoices at this email address')"
        :state="billing.billingContactEmail !== ''"
        type="email">
      </b-form-input>

      <h1 class="question--marginTop questions__header">{{ $t("Discount for annual payment") }}</h1>
      <p>
        {{
          $t("If you choose to pay annually, you will pay the total fee yearly fee at once after implementation. This means less administration for us, and therefore you will receive a discount. The discount of 2% will be calculated over the total amount you pay annually.")
        }}
      </p>
      <div class="custom-control custom-switch">
        <input id="yearlyInvoice" v-model="billing.yearlyInvoice" class="custom-control-input" type="checkbox"/>
        <label class="custom-control-label" for="yearlyInvoice">{{
            $t("Yes, we would like to pay the monthly fee in advance per year and receive a 2% discount")
          }}</label>
      </div>

      <div id="ibanDetails" :class="{hidden: !showBankAccountDetails}">
        <h1 class="question--marginTop questions__header">{{ $t("Bank account details") }}</h1>
        <p>
          {{
            $t("In the agreement you signed, you agreed to a direct debit of the monthly fee for the Dyflexis Scheduling System.")
          }}<br/><br/>
          Dyflexis B.V.<br/>
          Binckhorstlaan 36<br/>
          2516 BE Den Haag<br/><br/>
          Incassant ID: NL73ZZZ516169390001
        </p>
        <div class="hide">
          <h2 class="questions__subheader">
            {{ $t("Your IBAN") }}
          </h2>
          <b-form-input
            id="accountIban"
            v-model="billing.accountIban"
            :state="this.ibanCorrect()"
            class="form-control"
            type="text"
          />
        </div>

        <div :class="{hidden: !showAbroadFields}">
          <h2 class="questions__subheader">{{ $t("BIC code") }}</h2>
          <input
            id="accountBic"
            v-model="billing.accountBic"
            class="form-control"
            type="text"/>
        </div>
      </div>
    </div>

    <input :value="$t('Save')" type="button" @click="handleSave"/>
    <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "@/GlobalFunctions";

export default {
  name: 'Billing',
  data() {
    return {
      displayFrontend: false,
      showBillingAddress: true,
      safeFreeze: false,
      showBankAccountDetails: true,
      showBillingRemarks: false,
      showSaveStatus: false,
      showAbroadFields: false,
      showBillingContactFields: false,
      vatCorrect: false,
      vatComment: '',
      billing: {
        accountName: '',
        billingRemarks: '',
        billingRemarksData: false,
        accountBillingStreet: '',
        accountBillingCity: '',
        accountBillingPostalCode: '',
        accountBillingCountry: '',
        accountYearlyInvoice: '',
        accountVatNumber: '',
        accountIban: '',
        accountBic: '',
        poNumber: '',
        yearlyInvoice: '',
        billingContactSalutation: '',
        billingContactFirstName: '',
        billingContactLastName: '',
        billingContactPhone: '',
        billingContactEmail: '',
      },
    };
  },
  async beforeMount() {
    const result = await SystemRequestService.getProject(this.$route.params.hashedId);
    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return;
    }

    this.billing.accountName = result.accountName;
    this.billing.billingRemarks = result.billingRemarks;
    this.billing.accountBillingStreet = result.accountBillingStreet;
    this.billing.accountBillingCity = result.accountBillingCity;
    this.billing.accountBillingPostalCode = result.accountBillingPostalCode;
    this.billing.accountBillingCountry = result.accountBillingCountry;
    this.billing.poNumber = result.poNumber;
    this.billing.yearlyInvoice = result.accountYearlyInvoice;
    this.billing.accountVatNumber = result.accountVatNumber;
    if (result.billingContactFirstName) {
      this.billing.billingContactSalutation = result.billingContactSalutation;
      this.billing.billingContactFirstName = result.billingContactFirstName;
      this.billing.billingContactLastName = result.billingContactLastName;
      this.billing.billingContactPhone = result.billingContactPhone;
      this.billing.billingContactEmail = result.billingContactEmail;
    }

    if ("accountIban" in result) {
      this.billing.accountIban = result.accountIban;
      this.billing.accountBic = result.accountBic;
    } else {
      this.showBankAccountDetails = false;
    }

    if (this.billing.billingContactSalutation == null) {
      this.billing.billingContactSalutation = '-';
    }

    if (this.billing.billingRemarks !== null) {
      this.billing.billingRemarksData = true;
      this.showBillingRemarks = true;
      this.showBillingAddress = false;
      this.showBankAccountDetails = false;
    }

    if (this.billing.accountBillingCountry !== 'Netherlands') {
      this.showAbroadFields = true;
    }

    this.billing.accountBillingCountry = this.$t(this.billing.accountBillingCountry);

    if (result.externalPayment === false) {
      this.showBillingContactFields = true;
    }

    if (this.billing.accountVatNumber !== null) {
      this.vatCorrect = true;
    }

    GlobalFunctions.setReadOnly(result.isSigned);
    localStorage.setItem('startingObject', JSON.stringify(this.billing));
    this.displayFrontend = true;
  },
  beforeRouteLeave: function (to, from, next) {
    if (!GlobalFunctions.acceptPageLeave(this.billing, this.$t("You have made changes without saving them. Press cancel to ignore these changes and leave the page."))) {
      this.handleSave(false);
    }
    next();
  },
  methods: {
    advancedBilling($event) {
      if ($event) {
        this.showBillingRemarks = true;
        this.showBankAccountDetails = false;
        this.showBillingAddress = false;
        this.billing.accountIban = null;

        return;
      }

      if (this.billing.billingRemarks) {
        const deleteRemarks = confirm(this.$t("If you continue the comments entered will be deleted, press cancel to prevent this."));
        if (deleteRemarks !== true) {
          return;
        }
      }

      this.billing.billingRemarks = null;
      this.showBillingRemarks = false;
      this.showBankAccountDetails = true;
      this.showBillingAddress = true;
    },
    async handleSave(saveInitialObject) {
      this.safeFreeze = true;

      const EuMembers = ['Austria','Belgium','Bulgaria','Cyprus','Czech Republic','Denmark','Estonia','Finland','France','Germany','Greece','Hungary','Ireland','Italy','Latvia','Lithuania','Luxembourg','Malta','Poland','Portugal','Romania','Slovakia','Slovenia','Spain','Sweden'];
      if (
        this.billing.accountVatNumber != null
        && this.showBillingContactFields
        && this.vatCorrect === null
        && EuMembers.includes(this.billing.accountBillingCountry)
        && !await this.vatCorrectCheckEU()
      ) {
        alert('VAT number is not correct');
        this.safeFreeze = false;
        return;
      }

      if (this.showBillingContactFields) {
        if (this.showBankAccountDetails && (this.billing.accountIban === null || this.ibanCorrect() === false)) {
          alert(this.$t("IBAN incorrect"));
          this.safeFreeze = false;
          return;
        }

        if (this.billing.billingContactEmail === '' || this.billing.billingContactFirstName === null ||
          this.billing.billingContactLastName === null) {
          alert(this.$t("All financial contact fields are required"));
          this.safeFreeze = false;
          return;
        }
      }

      const result = await SystemRequestService.updateProject(this.billing, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      if (saveInitialObject !== false) {
        localStorage.setItem('startingObject', JSON.stringify(this.billing));
      }

      this.safeFreeze = false;
      this.showSaveStatus = true;
      setTimeout(() => {
        this.showSaveStatus = false;
      }, 3000);
    },
    async vatCorrectCheckEU() {
      const vatNumber = this.billing.accountVatNumber;

      if (!vatNumber.charAt(0).match(/[a-z]/i) || !vatNumber.charAt(1).match(/[a-z]/i)) {
        this.vatCorrect = false;
        this.vatComment = this.$t('The first two characters must represent the country of the VAT number. E.g. "FR", "DE" or "IT".');
        return this.vatCorrect;
      }

      const countryCode = vatNumber.slice(0, 2);
      const vatCode = vatNumber.slice(2);

      const response = await fetch('https://ec.europa.eu/taxation_customs/vies/rest-api/ms/'+ countryCode +'/vat/' + vatCode);
      const json = await response.json();

      if (!json.isValid) {
        this.vatCorrect = false;
        this.vatComment = this.$t('The provided VAT number is not correct.');
        return this.vatCorrect;
      }

      this.vatCorrect = true;
      this.vatComment = this.$t('This VAT is verified and registered to:  {legalName}', {legalName: json.name});
      return this.vatCorrect;
    },
    ibanCorrect() {
      const CODE_LENGTHS = {
        AD: 24,
        AE: 23,
        AT: 20,
        AZ: 28,
        BA: 20,
        BE: 16,
        BG: 22,
        BH: 22,
        BR: 29,
        CH: 21,
        CY: 28,
        CZ: 24,
        DE: 22,
        DK: 18,
        DO: 28,
        EE: 20,
        ES: 24,
        FI: 18,
        FO: 18,
        FR: 27,
        GB: 22,
        GI: 23,
        GL: 18,
        GR: 27,
        GT: 28,
        HR: 21,
        HU: 28,
        IE: 22,
        IL: 23,
        IS: 26,
        IT: 27,
        JO: 30,
        KW: 30,
        KZ: 20,
        LB: 28,
        LI: 21,
        LT: 20,
        LU: 20,
        LV: 21,
        MC: 27,
        MD: 24,
        ME: 22,
        MK: 19,
        MR: 27,
        MT: 31,
        MU: 30,
        NL: 18,
        NO: 15,
        PK: 24,
        PL: 28,
        PS: 29,
        PT: 25,
        QA: 29,
        RO: 24,
        RS: 22,
        SA: 24,
        SE: 24,
        SI: 19,
        SK: 24,
        SM: 27,
        TN: 24,
        TR: 26,
        AL: 28,
        BY: 28,
        CR: 22,
        EG: 29,
        GE: 22,
        IQ: 23,
        LC: 32,
        SC: 31,
        ST: 25,
        SV: 28,
        TL: 23,
        UA: 29,
        VA: 22,
        VG: 24,
        XK: 20,
      };

      const iban = String(this.billing.accountIban).toUpperCase().replace(/[^A-Z0-9]/g, '');
      const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
      if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
        return false;
      }
      const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, letter => letter.charCodeAt(0) - 55);

      let checksum = digits.slice(0, 2);
      let fragment;
      for (let offset = 2; offset < digits.length; offset += 7) {
        fragment = String(checksum) + digits.substring(offset, offset + 7);
        checksum = parseInt(fragment, 10) % 97;
      }

      if (checksum === 1) {
        return true;
      }

      return false;
    },
  },
};
</script>
